import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { signIn, signUp, triggerHaptic } from '@repo/shared/client';
import { errorTracker } from '@repo/shared/common';
import { sessionQueryKey } from '~/lib/queries/session';
import type { AuthMode, AuthFormFieldErrors } from '~/routes/_auth/AuthForm';

// After authentication completes, send the user to the post-sign-in landing
// route. `/post-sign-in` fires sign-in analytics once and then forwards into
// the app (resolveLandingRoute → `/modeling` or `/uploads`) — it's the single
// seam for post-auth routing (resolve/create org, fork onboarding). This matches
// the Google flow's `callbackURL` so both auth paths land in the same place.
const POST_SIGNIN_REDIRECT = '/post-sign-in';

// BetterAuth's default minimum password length. The server doesn't override it,
// so mirror it here for the client-side hint and pre-submit validation.
const MIN_PASSWORD_LENGTH = 8;

// Pragmatic email shape check — the real validation is server-side; this just
// catches obvious typos before a round-trip.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface UseAuthFormReturn {
  readonly name: string;
  readonly email: string;
  readonly password: string;
  readonly error: string;
  readonly fieldErrors: AuthFormFieldErrors;
  readonly loading: boolean;
  /**
   * True when a sign-up failed specifically because an account already exists at
   * this email. The form uses it to offer a "sign in instead" link (deep-linked
   * to /sign-in with the typed email) rather than the generic failure copy.
   */
  readonly emailAlreadyExists: boolean;
  readonly onNameChange: (value: string) => void;
  readonly onEmailChange: (value: string) => void;
  readonly onPasswordChange: (value: string) => void;
  readonly onSubmit: (e: FormEvent) => void;
}

/**
 * Email/password auth flow for both the sign-in and sign-up variants. Owns the
 * controlled field state, runs lightweight client-side validation, calls the
 * matching BetterAuth client method, and on success navigates to the
 * post-sign-in landing. BetterAuth returns `{ error }` rather than throwing for
 * expected failures (bad credentials, duplicate email), so those surface as a
 * translated top-level message; unexpected throws are reported to the error
 * tracker. The optional Google path lives in `useGoogleSignIn`.
 *
 * `initialName`/`initialEmail` seed the controlled fields — used by the sign-up
 * route to prefill from an invite's `?name=`/`?email=` search params so an
 * invited teammate signs up with the address they were invited at. They only set
 * the initial value; the fields stay fully editable.
 */
export function useAuthForm(
  mode: AuthMode,
  initialName = '',
  initialEmail = '',
): UseAuthFormReturn {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isSignUp = mode === 'sign-up';

  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<AuthFormFieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);

  function validate(): AuthFormFieldErrors {
    const errors: { name?: string; email?: string; password?: string } = {};

    if (isSignUp && name.trim() === '') {
      errors.name = t('auth.nameRequired');
    }

    if (email.trim() === '') {
      errors.email = t('auth.emailRequired');
    } else if (!EMAIL_RE.test(email.trim())) {
      errors.email = t('auth.emailInvalid');
    }

    if (password === '') {
      errors.password = t('auth.passwordRequired');
    } else if (isSignUp && password.length < MIN_PASSWORD_LENGTH) {
      errors.password = t('auth.passwordTooShort');
    }

    return errors;
  }

  async function submitAsync(): Promise<void> {
    triggerHaptic();
    setError('');
    setEmailAlreadyExists(false);

    const validationErrors = validate();
    setFieldErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setLoading(true);
    try {
      const { error: authError } = isSignUp
        ? await signUp.email({
          name: name.trim(),
          email: email.trim(),
          password,
        })
        : await signIn.email({
          email: email.trim(),
          password,
        });

      if (authError) {
        const code =
          'code' in authError && typeof authError.code === 'string'
            ? authError.code
            : 'unknown';
        errorTracker.logMessage(`${mode} auth error`, { code }, 'info');
        // A duplicate-email sign-up gets a specific, helpful message + a
        // "sign in instead" affordance rather than the generic failure copy.
        if (isSignUp && code === 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL') {
          setEmailAlreadyExists(true);
          setError(t('auth.emailAlreadyExists'));
        } else {
          setError(isSignUp ? t('auth.signUpError') : t('auth.signInError'));
        }
        setLoading(false);
        return;
      }

      // Drop the cached (signed-out) session so the post-sign-in auth gate
      // refetches the freshly-authenticated one instead of the stale null left
      // by the /sign-in gate — otherwise `ensureQueryData` would return that
      // null and bounce the user straight back to sign-in.
      queryClient.removeQueries({ queryKey: sessionQueryKey });
      void navigate({ to: POST_SIGNIN_REDIRECT, replace: true });
      // Leave `loading` true on success — the navigation unmounts this form.
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      errorTracker.captureException(wrapped, { context: `${mode} email auth` });
      setError(isSignUp ? t('auth.signUpError') : t('auth.signInError'));
      setLoading(false);
    }
  }

  return {
    name,
    email,
    password,
    error,
    fieldErrors,
    loading,
    emailAlreadyExists,
    onNameChange: (value: string) => setName(value),
    onEmailChange: (value: string) => setEmail(value),
    onPasswordChange: (value: string) => setPassword(value),
    onSubmit: (e: FormEvent) => {
      e.preventDefault();
      void submitAsync();
    },
  };
}
